import { Container, Graphics, Text, type TextStyleOptions } from 'pixi.js';
import type { Scene } from '../core/Scene';
import type { InputSnapshot } from '../input/actions';
import type { GameContext } from '../GameContext';
import { Palette } from '../render/Palette';
import {
  META_UPGRADES, buyMetaUpgrade, canBuyMetaUpgrade, metaUpgradeCost, metaUpgradeRank,
} from '../sim/meta/metaUpgrades';
import { fitScene } from '../ui/fitLayout';
import { FocusList } from '../ui/FocusList';
import { logJournal } from '../meta/journal';

const ROW_W = 700;
const ROW_H = 56;
const GAP = 12;
const MIN_W = ROW_W + 40;
/** Layout budget: scale stays 1.0 on 1280×800 AND 1280×720-with-chrome. */
const MIN_H = 100 + META_UPGRADES.length * (ROW_H + GAP) + 90;

/**
 * THE FORGE — permanent shard-bought upgrade tracks, one full-width row per
 * track (the old five-across cells were width-starved; HANDOFF records the
 * "(not enough)" suffix already being cut for space). This scene is the
 * future home of the per-character skill trees (CHARACTER_DEPTH_PLAN) —
 * deliberately a flat list until that plan lands. Layout rebuilds on
 * screen-size change (syncLayout).
 */
export class ForgeScene implements Scene {
  readonly root = new Container();
  readonly blocksUpdateBelow = true;

  private readonly rows = new FocusList(META_UPGRADES.length);
  private acceptDelay = 8;
  private dynamic = new Container();
  private dirty = true;
  private lastDevice: 'kb' | 'pad' = 'kb';

  private vw = 0;
  private vh = 0;
  private lastW = 0;
  private lastH = 0;
  private rowsY = 0;

  constructor(private readonly ctx: GameContext) {
    this.root.addChild(this.dynamic);
  }

  enter(): void {}
  exit(): void {}

  private syncLayout(): void {
    const { width, height } = this.ctx.app.screen;
    if (width === this.lastW && height === this.lastH) return;
    this.lastW = width;
    this.lastH = height;
    const { w, h } = fitScene(this.root, width, height, MIN_W, MIN_H);
    this.vw = w;
    this.vh = h;
    this.rowsY = 108;
    this.dirty = true;
  }

  update(_dt: number, input: InputSnapshot): void {
    this.syncLayout();
    if (input.device !== this.lastDevice) {
      this.lastDevice = input.device;
      this.dirty = true;
    }
    if (this.acceptDelay > 0) {
      this.acceptDelay--;
      return;
    }
    if (this.rows.move(input.navY)) this.dirty = true;
    if (input.confirmPressed) {
      const def = META_UPGRADES[this.rows.focus]!;
      if (buyMetaUpgrade(this.ctx.profile, def.id) === 'ok') {
        this.dirty = true;
        logJournal(`forge: buy ${def.id} (◇${this.ctx.profile.data.shards})`);
      }
    }
    if (input.cancelPressed) this.ctx.scenes.pop();
  }

  render(_alpha: number): void {
    this.syncLayout();
    if (!this.dirty) return;
    this.dirty = false;
    this.redraw();
  }

  private redraw(): void {
    const profile = this.ctx.profile;
    const w = this.vw;
    this.dynamic.removeChildren().forEach((c) => c.destroy({ children: true }));
    const g = new Graphics();
    this.dynamic.addChild(g);
    g.rect(0, 0, w, this.vh).fill(Palette.bg);

    this.text('THE FORGE', w / 2, 24, {
      fontSize: 34, fontWeight: 'bold', fill: Palette.hudText, letterSpacing: 6,
    }, 0.5);
    this.text('permanent work, paid in shards — every track has a hard cap', w / 2, 64, {
      fontSize: 12, fill: Palette.hudDim,
    }, 0.5);

    const x0 = (w - ROW_W) / 2;
    this.text(`◇ ${profile.data.shards}`, x0 + ROW_W, 80, {
      fontSize: 16, fill: Palette.playerCool,
    }, 1);

    META_UPGRADES.forEach((def, i) => {
      const y = this.rowsY + i * (ROW_H + GAP);
      const rank = metaUpgradeRank(profile.data, def.id);
      const cost = metaUpgradeCost(profile.data, def.id);
      const buyable = canBuyMetaUpgrade(profile, def.id) === 'ok';
      const focused = i === this.rows.focus;

      g.roundRect(x0, y, ROW_W, ROW_H, 8)
        .fill({ color: Palette.hudPanel, alpha: 0.9 })
        .stroke({
          width: focused ? 2.5 : rank > 0 ? 2 : 1.5,
          color: focused ? Palette.focus : rank >= def.maxRank ? Palette.gold : Palette.hudDim,
          alpha: rank > 0 || buyable || focused ? 0.9 : 0.4,
        });

      this.text(
        `${def.name}  ${'●'.repeat(rank)}${'○'.repeat(def.maxRank - rank)}`,
        x0 + 18, y + 10,
        {
          fontSize: 14, fontWeight: 'bold',
          fill: rank > 0 ? Palette.hudText : Palette.hudDim,
        },
      );
      this.text(def.perRank, x0 + 18, y + 33, { fontSize: 12, fill: Palette.hudDim });

      // Full-width rows finally have room for honest affordability text.
      const right = cost === null
        ? 'MAX'
        : buyable ? `buy  ◇ ${cost}` : `◇ ${cost}  (not enough)`;
      this.text(right, x0 + ROW_W - 18, y + ROW_H / 2, {
        fontSize: 13,
        fill: cost === null ? Palette.gold : buyable ? Palette.playerCool : Palette.hudDim,
      }, 1, 0.5);
    });

    const hint = this.lastDevice === 'pad'
      ? 'navigate  [dpad]     buy  [Ⓐ]     back  [Ⓑ]'
      : 'navigate  [↑↓]     buy  [E]     back  [Esc]';
    this.text(hint, w / 2, this.vh - 30, { fontSize: 12, fill: Palette.hudDim }, 0.5);
  }

  private text(
    content: string, x: number, y: number,
    style: TextStyleOptions,
    anchorX = 0, anchorY = 0,
  ): Text {
    const t = new Text({ text: content, style: { fontFamily: 'monospace', ...style } });
    t.anchor.set(anchorX, anchorY);
    t.position.set(x, y);
    this.dynamic.addChild(t);
    return t;
  }
}
